import { PDF_DOCUMENT_OPTIONS } from '@chiroup/components/pdfDocumentOptions';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { Skeleton } from '../common/Skeleton';

// Self-host the pdf.js worker as a bundled Vite asset (`?url`) instead of a
// CDN. Vite emits it under /assets with a content hash and the correct
// JS MIME type, so it can't 404 to the SPA index.html fallback (which served
// text/html and tripped strict MIME checking). The pinned pdfjs-dist override
// keeps React-PDF and its worker on the same version so they cannot drift.
// Use the `legacy` build: patient phones run old browsers (e.g. Samsung
// Internet) missing Map.prototype.getOrInsertComputed, which the modern
// build calls unguarded. The vite config aliases the main-thread pdfjs-dist
// import to the legacy build for the same reason.
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Soft Card consent panel — pure UI, no auth opinion. Renders the PDF
// page-by-page, then a signature page; calls `onSubmit(signature)` once
// the patient agrees and signs. Used by both the Cognito-authed
// `ConsentRunner` and the validationCode-authed `/requests/:id`
// `StepConsent`.

export type ConsentPanelStatus = 'ready' | 'submitting' | 'submitted' | 'error';

type Props = {
  title: string;
  url: string | null;
  primaryColor: string;
  language: 'en' | 'es';
  status?: ConsentPanelStatus;
  onSubmit: (signature: string) => Promise<void> | void;
  onBack?: () => void;
  // Show a "Back" link at the top — only relevant when this panel is
  // mounted as a top-level page rather than a step inside a runner.
  showTopBack?: boolean;
  topBackHref?: string;
  // Custom verbiage for the success state (used after a fresh submit
  // to differentiate "just submitted" vs "already on file").
  submittedTitle?: string;
  submittedDetail?: string;
  exitLabel?: string;
  onExit?: () => void;
};

const verbiage = {
  en: {
    back: 'Back',
    prev: 'Prev',
    next: 'Next',
    page: 'Page',
    of: 'of',
    pleaseSign: 'Please sign below',
    iAgree: 'I have received, reviewed, and agree to this form.',
    clear: 'Clear signature',
    submit: 'Submit',
    submitted: 'Consent submitted',
    submittedDetail: 'Thank you. Your signed consent is on file.',
    errorTitle: 'Something went wrong',
    errorDetail: 'Please try again in a moment.',
  },
  es: {
    back: 'Volver',
    prev: 'Anterior',
    next: 'Siguiente',
    page: 'Página',
    of: 'de',
    pleaseSign: 'Por favor firme abajo',
    iAgree: 'He recibido, revisado y acepto este formulario.',
    clear: 'Borrar firma',
    submit: 'Enviar',
    submitted: '¡Consentimiento enviado!',
    submittedDetail: 'Gracias.',
    errorTitle: 'Algo salió mal',
    errorDetail: 'Por favor inténtelo de nuevo en unos momentos.',
  },
};

export const SoftConsentPanel: React.FC<Props> = ({
  title,
  url,
  primaryColor,
  language,
  status = 'ready',
  onSubmit,
  showTopBack,
  topBackHref,
  submittedTitle,
  submittedDetail,
  exitLabel,
  onExit,
}) => {
  const v = useMemo(() => verbiage[language], [language]);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [signed, setSigned] = useState(false);
  const sigPad = useRef<SignatureCanvas>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;

      try {
        const pad = sigPad.current as any;

        if (pad?.off && typeof pad.off === 'function') pad.off();
      } catch {
        /* noop */
      }
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => setNumPages(n);

  const showPrev = pageNumber > 1;
  const onSignaturePage = numPages > 0 && pageNumber > numPages;
  const isSubmitting = status === 'submitting';

  const handleClear = () => {
    try {
      sigPad.current?.clear();
      setSigned(false);
    } catch {
      setSigned(false);
    }
  };

  const handleSubmit = async () => {
    if (!signed || isSubmitting || !url) return;

    let signature: string | undefined;

    try {
      signature = sigPad.current?.getTrimmedCanvas()?.toDataURL('image/png');
    } catch (err) {
      Sentry.captureException(err);

      return;
    }

    if (!signature) return;

    await onSubmit(signature);
  };

  if (status === 'submitted') {
    return (
      <div
        className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
        style={{ backgroundColor: `${primaryColor}12` }}
      >
        <div
          className="size-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${primaryColor}25`, color: primaryColor }}
          aria-hidden="true"
        >
          <CheckCircleSolid className="size-7" />
        </div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {submittedTitle ?? v.submitted}
        </h1>
        <p className="max-w-prose text-sm text-gray-600 dark:text-gray-300">
          {submittedDetail ?? v.submittedDetail}
        </p>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="mt-2 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {exitLabel ?? v.back}
          </button>
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="portal-card p-6 text-center">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {v.errorTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{v.errorDetail}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {showTopBack && topBackHref && (
        <div className="mb-4">
          <a
            href={topBackHref}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ChevronLeftIcon className="size-4" />
            {v.back}
          </a>
        </div>
      )}

      <div className="portal-card p-4 sm:p-5 flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 truncate">
            {title}
          </h1>
          {numPages > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
              {onSignaturePage ? v.pleaseSign : `${v.page} ${pageNumber} ${v.of} ${numPages}`}
            </span>
          )}
        </div>

        {!onSignaturePage && url && (
          <div className="overflow-auto bg-gray-100 dark:bg-white/5 rounded-xl p-2 max-h-[70vh]">
            <Document
              file={url}
              options={PDF_DOCUMENT_OPTIONS}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={err => Sentry.captureException(err)}
              loading={
                <div className="flex flex-col gap-3 p-4">
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-64 w-full" />
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={Math.min(800, typeof window !== 'undefined' ? window.innerWidth - 80 : 800)}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
          </div>
        )}

        {onSignaturePage && (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
              {v.iAgree}
            </p>
            <div className="border-2 border-gray-300 dark:border-white/15 rounded-xl bg-white w-full max-w-md h-44 overflow-hidden">
              <SignatureCanvas
                canvasProps={{ className: 'w-full h-full' }}
                ref={sigPad}
                clearOnResize={false}
                onEnd={() => {
                  if (isMounted.current) setSigned(true);
                }}
              />
            </div>
            {signed && (
              <button
                type="button"
                onClick={handleClear}
                className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                {v.clear}
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-950/5 dark:border-white/10">
          <button
            type="button"
            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
            disabled={!showPrev || isSubmitting}
            className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-30"
          >
            {v.prev}
          </button>
          {!onSignaturePage ? (
            <button
              type="button"
              onClick={() => setPageNumber(p => p + 1)}
              disabled={!numPages}
              className="inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {v.next}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!signed || isSubmitting}
              className="inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {v.submit}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
